import {
    InfoCheckbox,
    preventSubmit,
    Spinner,
    useAnalyticsActionTracker,
    useAnalyticsFormTracker,
    ValidatedInput,
} from '@cp-shared-19/frontend-ui';
import { Button, Fieldset, Form, Layout } from '@vwfs-bronson/bronson-react';
import { contactDetailsCheckPagePath } from 'components/navigation/paths';
import { Formik } from 'formik';
import { isEmpty } from 'lodash';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { RegistrationErrorNotification } from '../../registration-error';
import { RegistrationLegalFields } from '../RegistrationFormDataTypes';
import { useRegistrationSubmission } from '../useRegistrationSubmission';
import { validationSchema } from './validation';

export const testIds: { [key: string]: string } = {
    contractNumber: 'contract-number',
    customerNumber: 'customer-number',
    companyName: 'company-name',
    siren: 'siren',
    confirmPrivacyPolicy: 'confirm-privacy-policy',
    confirmUsageAgreement: 'confirm-usage-agreement',
    submitButton: 'submit-registration-button',
};

type RegistrationLegalProps = {
    contractNumber: string;
};

export const RegistrationLegal: React.FC<RegistrationLegalProps> = ({ contractNumber }) => {
    const { t } = useTranslation(['registration', 'loader']);
    const { onAction } = useAnalyticsActionTracker('onFormValidationErrorConfirmIdentity');
    const {
        onError: trackOnError,
        onSuccess: trackOnSuccess,
        onTyping,
        onSubmit: trackOnSubmit,
    } = useAnalyticsFormTracker({
        confirmError: 'onConfirmIdentiyAuthFailed',
        confirmSuccess: 'onConfirmConfirmIdentiy',
        startTyping: 'onStartTypingConfirmIdentity',
    });
    const onSubmissionSuccess = useMemo(
        () => (): void => {
            trackOnSuccess();
            window.location.assign(contactDetailsCheckPagePath());
        },
        [trackOnSuccess],
    );
    const onSubmissionError = useMemo(
        () => (): void => {
            trackOnError();
        },
        [trackOnError],
    );
    const { submit, isLoading, errorCode } = useRegistrationSubmission(onSubmissionSuccess, onSubmissionError, true);

    const translationPrefix = 'registration-form.legal.input-labels';

    const getErrors = (errors: { [k: string]: string | undefined }) => Object.keys(errors).join(`, `);

    const initialListErrors =
        'contractNumber, customerName, companyName, siren, confirmTermsAndConditions, confirmPrivacyPolicy';

    const onSubmit = (values: RegistrationLegalFields) => {
        trackOnSubmit();
        const { contractNumber, customerNumber, companyName, siren } = values;
        submit({
            contractNumber,
            customerNumber,
            companyName,
            siren,
        });
    };

    const initialValues: RegistrationLegalFields = {
        contractNumber: contractNumber,
        customerNumber: '',
        companyName: '',
        siren: '',
        confirmPrivacyPolicy: false,
        confirmTermsAndConditions: false,
    };

    return (
        <>
            {isLoading && (
                <Spinner center>
                    <p>{t('genericText', { ns: 'loader' })}</p>
                </Spinner>
            )}
            <Layout.Item>
                <RegistrationErrorNotification errorCode={errorCode} />
            </Layout.Item>
            <Layout.Item default={'1/1'}>
                <Formik initialValues={initialValues} validationSchema={validationSchema(t)} onSubmit={onSubmit}>
                    {(formik) => (
                        <Form
                            onSubmit={(e) => preventSubmit(e)}
                            onChange={() => {
                                onTyping(formik.errors, formik.touched);
                            }}
                        >
                            <Fieldset>
                                <Fieldset.Row>
                                    <ValidatedInput
                                        requiredText=" *"
                                        required={true}
                                        aria-required={true}
                                        className="c-form-field--no-floating"
                                        label={t(`${translationPrefix}.customer-number`)}
                                        name="customerNumber"
                                        tooltip={t('registration-form.head.info-fill-data')}
                                        tooltipTitle={t(`${translationPrefix}.customer-number-tooltip-title`)}
                                        testId={testIds.customerNumber}
                                        inputId="customer-number-reqistration-legal"
                                    />
                                </Fieldset.Row>
                                <Fieldset.Row>
                                    <ValidatedInput
                                        requiredText=" *"
                                        required={true}
                                        aria-required={true}
                                        className="c-form-field--no-floating"
                                        label={t(`${translationPrefix}.company-name`)}
                                        name="companyName"
                                        testId={testIds.companyName}
                                        inputId="company-name-reqistration-legal"
                                        autocomplete="organizaition"
                                    />
                                </Fieldset.Row>
                                <Fieldset.Row>
                                    <ValidatedInput
                                        requiredText=" *"
                                        required={true}
                                        aria-required={true}
                                        className="c-form-field--no-floating"
                                        label={t(`${translationPrefix}.siren`)}
                                        name="siren"
                                        testId={testIds.siren}
                                        inputId="siren-reqistration-legal"
                                    />
                                </Fieldset.Row>
                                <InfoCheckbox
                                    required={true}
                                    aria-required={true}
                                    preLinkLabel={t(`${translationPrefix}.agreements.privacy-policy.label`)}
                                    linkLabel={t(`${translationPrefix}.agreements.privacy-policy.link`)}
                                    infoLinkHref={'https://www.vwfs.fr/espace-client/politique-donnees.html'}
                                    fieldName={'confirmPrivacyPolicy'}
                                    testId={testIds.confirmPrivacyPolicy}
                                ></InfoCheckbox>
                                <InfoCheckbox
                                    required={true}
                                    aria-required={true}
                                    preLinkLabel={t(`${translationPrefix}.agreements.terms-and-conditions.label`)}
                                    linkLabel={t(`${translationPrefix}.agreements.terms-and-conditions.link`)}
                                    infoLinkHref={'https://www.vwfs.fr/espace-client/CGU-espace-client.html'}
                                    fieldName={'confirmTermsAndConditions'}
                                    testId={testIds.confirmUsageAgreement}
                                ></InfoCheckbox>
                                <Fieldset.Row>
                                    <Button
                                        full={true}
                                        onClick={() => {
                                            formik.submitForm();
                                            if (!isEmpty(formik.errors) || isEmpty(formik.touched)) {
                                                const errorToString = getErrors(formik.errors).toString();
                                                if (!errorToString) {
                                                    onAction(initialListErrors);
                                                } else onAction(getErrors(formik.errors));
                                            }
                                        }}
                                        testId={testIds.submitButton}
                                    >
                                        {t(`registration-form.button-labels.confirm`)}
                                    </Button>
                                </Fieldset.Row>
                            </Fieldset>
                        </Form>
                    )}
                </Formik>
            </Layout.Item>
        </>
    );
};
